import axios from 'axios';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';

function HeaderSearchbar() {
  const [movieResults, setMovieResults] = useState([]);
  const [tvResults, setTvResults] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentTimeout, setCurrentTimeout] = useState(null);
  const [loading, setLoading] = useState(false);

  const tmdbApiKey = process.env.REACT_APP_TMDB_API_KEY;
  const videoGamesApiKey = process.env.REACT_APP_RAWGIO_API_KEY;
  const server_url = process.env.REACT_APP_API_URI;
  const videoGameOrderingCriteria = '-playtime,-added';

  const handleUserSearchTextChange = (e) => {
    const searchText = e.target.value.trim(); // Trim spaces to avoid empty input showing dropdown
    setUserSearchText(searchText);

    if (searchText.length > 0) {
      setShowDropdown(true);
      handleSearch(searchText);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSearch = (searchText) => {
    if (currentTimeout) clearTimeout(currentTimeout);

    setCurrentTimeout(
      setTimeout(async () => {
        setLoading(true);
        try {
          const [gamesRes, moviesRes, tvRes, productsRes] =
            await Promise.allSettled([
              axios.get(
                `https://api.rawg.io/api/games?key=${videoGamesApiKey}&ordering=${videoGameOrderingCriteria}&search=${searchText}&page_size=5`,
              ),
              axios.get(
                `https://api.themoviedb.org/3/search/movie?api_key=${tmdbApiKey}&query=${searchText}&include_adult=false&language=en-US&page=1`,
              ),
              axios.get(
                `https://api.themoviedb.org/3/search/tv?api_key=${tmdbApiKey}&query=${searchText}&include_adult=false&language=en-US&page=1`,
              ),
              axios.get(
                `${server_url}/my-products/search-products/${searchText}`,
              ),
            ]);

          setGameResults(
            gamesRes.status === 'fulfilled'
              ? gamesRes.value.data.results || []
              : [],
          );
          setMovieResults(
            moviesRes.status === 'fulfilled'
              ? moviesRes.value.data.results || []
              : [],
          );
          setTvResults(
            tvRes.status === 'fulfilled' ? tvRes.value.data.results || [] : [],
          );
          setProductResults(
            productsRes.status === 'fulfilled'
              ? productsRes.value.data.data || []
              : [],
          );
        } catch (error) {
          console.error('Search API Error:', error);
        } finally {
          setLoading(false); // Stop loader
        }
      }, 500),
    );
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <input
        type="search"
        className="w-full h-12 px-4 text-lg text-gray-800 bg-white rounded-full border border-gray-300 
        focus:outline-none focus:ring-0 shadow-sm"
        placeholder="Search movies, games, products..."
        value={userSearchText}
        onChange={handleUserSearchTextChange}
        onFocus={() => setShowDropdown(true)}
      />

      {showDropdown && (
        <div className="absolute left-0 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg  max-h-64 overflow-y-auto transition-all">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-4 border-secondary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {movieResults.length > 0 && (
                <div>
                  <h3 className="px-4 py-2 text-sm font-semibold text-secondary bg-primary">
                    Movies
                  </h3>
                  {movieResults.map((item) => (
                    <NavLink
                      to={`/rate/movie/${item.id}`}
                      key={item.id}
                      className="block px-4 py-3 hover:bg-secondary/50"
                    >
                      <div className="search-image-and-title">
                        <img
                          src={`https://image.tmdb.org/t/p/original/${item.backdrop_path}`}
                          className="searchbar-image"
                          alt={item.title}
                        />
                        <div>
                          <b>{item.title}</b> <br />
                          Movie
                          <br />
                          Released: {item.release_date?.substring(0, 4)}
                        </div>
                      </div>
                    </NavLink>
                  ))}
                </div>
              )}

              {tvResults.length > 0 && (
                <div>
                  <h3 className="px-4 py-2 text-sm font-semibold text-secondary bg-primary">
                    TV Shows
                  </h3>
                  {tvResults.map((item) => (
                    <NavLink
                      to={`/rate/tv/${item.id}`}
                      key={item.id}
                      className="block px-4 py-3 hover:bg-secondary/50"
                    >
                      <div className="search-image-and-title">
                        <img
                          src={`https://image.tmdb.org/t/p/original/${item.backdrop_path}`}
                          className="searchbar-image"
                          alt="Not Found"
                        />
                        <div>
                          <b>{item.name}</b> <br />
                          TV Show
                          <br />
                          Released: {item.release_date?.substring(0, 4)}
                        </div>
                      </div>
                    </NavLink>
                  ))}
                </div>
              )}

              {gameResults.length > 0 && (
                <div>
                  <h3 className="px-4 py-2 text-sm font-semibold text-secondary bg-primary">
                    Video Games
                  </h3>
                  {gameResults.map((item) => (
                    <NavLink
                      to={`/rate/game/${item.id}`}
                      key={item.id}
                      className="block px-4 py-3 hover:bg-secondary/50"
                    >
                      <div className="search-image-and-title">
                        <img
                          src={item.background_image}
                          className="searchbar-image"
                          alt="Not Found"
                        />

                        <div>
                          <b>{item.name}</b> <br />
                          Video Game
                          <br />
                          Released: {item.release_date?.substring(0, 4) || '-'}
                        </div>
                      </div>
                    </NavLink>
                  ))}
                </div>
              )}

              {productResults.length > 0 && (
                <div>
                  <h3 className="px-4 py-2 text-sm font-semibold text-secondary bg-primary">
                    Products
                  </h3>
                  {productResults.map((item) => (
                    <NavLink
                      to={`/rate/${item.categoryName.toLowerCase()}/${item.contentId}`}
                      key={item.id}
                      className="block px-4 py-3 hover:bg-secondary/50"
                    >
                      <div className="search-image-and-title">
                        <div>
                          <b>{item.contentName}</b> <br />
                          {/* Product */}
                          Category: {item.categoryName}
                        </div>
                      </div>
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderSearchbar;
